import { Action } from 'redux-actions'
import { all, call, put, takeEvery } from 'redux-saga/effects'
import { EditUserPreferences$Request, GetUserPreferences$Request } from '../../../services/api.types'
import {
  cancelUserPreferences,
  editUserPreferences,
  editUserPreferencesFail,
  editUserPreferencesSuccess,
  getUserPreferences,
  getUserPreferencesFail,
  getUserPreferencesSuccess,
} from './NotificationPreferencesActions'
import api from '../../../services/api.services'
import { INotification } from '../../../common/notifications/NotificationReducers'
import { t } from 'ttag'
import { push } from 'connected-react-router'
import { ROUTES } from '../../../common/Routes/routePaths'
import { addNotification, clearNotification } from '../../../common/notifications/NotificationActions'
import { getWhoAmI } from '../../../common/auth/authActions'
import { isNative } from '../../../common/helpers/isNative'
import { askPushNotificationPermissions, setUpPushNotifications } from '../../../services/pushNotifications'
import { announce } from '../../../components/a11y/useAnnouncer'

function* sagaGetUserPreferences(action: Action<GetUserPreferences$Request>) {
  yield put({ type: String(clearNotification) })
  try {
    const { preferences } = yield call(api.notifications.getUserPreferences, action.payload)
    yield put({ type: String(getUserPreferencesSuccess), payload: preferences })
    yield put({ type: String(getWhoAmI) })
  } catch (e: any) {
    console.log('GetUserPreferencesFail: ', e)
    yield put({ type: String(getUserPreferencesFail) })
  }
}

function* sagaEditUserPreferences(action: Action<EditUserPreferences$Request & { hasPushNotifications: boolean }>) {
  try {
    yield call(api.notifications.editUserPreferences, action.payload)
    yield put({ type: String(getUserPreferences) })
    yield put({ type: String(editUserPreferencesSuccess) })

    if (action.payload.hasPushNotifications) {
      if (isNative()) {
        try {
          yield askPushNotificationPermissions()
          yield setUpPushNotifications()
        } catch (e: any) {
          console.error('setUpPushNotifications failed: ', e)
        }
      }
    }

    if (action.payload.isDuringRegistration) {
      yield put({ type: String(getWhoAmI) })
    } else {
      const message = t`Notification preferences have been saved!`
      const notification: INotification = {
        type: 'success',
        content: message,
      }
      // The success banner is polite and renders after navigating to the inbox;
      // a polite region inserted during a route change is often not announced.
      // Announce it assertively via the persistent live region so screen reader
      // users reliably hear the save confirmation (WCAG 3.3.1). The announcer
      // node lives on document.body, so it survives the navigation below.
      announce(message, 'assertive')
      yield put(push(ROUTES.NOTIFICATIONS.to, { notification }))
    }
  } catch (e: any) {
    console.log('EditUserPreferencesFail: ', e)
    const notification: INotification = {
      type: 'warning',
      content: t`Request failed. Please try again!`,
    }
    yield put({ type: String(addNotification), payload: notification })
    yield put({ type: String(editUserPreferencesFail) })
  }
}

function* sagaCancelUserPreferences() {
  yield put(push(ROUTES.NOTIFICATIONS.to))
}

export function* userPreferencesSagas() {
  yield all([takeEvery(String(getUserPreferences), sagaGetUserPreferences)])
  yield all([takeEvery(String(editUserPreferences), sagaEditUserPreferences)])
  yield all([takeEvery(String(cancelUserPreferences), sagaCancelUserPreferences)])
}
